import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  InboxOutlined,
  PlusOutlined,
  SaveOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import {
  approveAssessment,
  archiveAssessment,
  assessmentPdfUrl,
  deleteAssessment,
  getAssessment,
  getMeta,
  reviseAssessment,
  updateAssessment
} from "./api";
import {
  RISK_COLORS,
  assessmentStatusLabels,
  formatDateTime,
  measureLabels,
  riskLabels
} from "./format";
import "./safety.css";

const LEVELS = [1, 2, 3];

/** Same scale as utils/safety/calc.js#riskScore. */
const levelOf = (probability, severity) => {
  const score = probability * severity;
  return score >= 6 ? "high" : score >= 3 ? "medium" : "low";
};

const dayValue = value => (value ? dayjs(value) : null);
const dayString = value => (value ? value.format("YYYY-MM-DD") : "");

const HazardCard = ({ hazard, index, editable, onChange, onRemove }) => {
  const { t } = useTranslation("dashboard");
  const measures = measureLabels(t);
  const risks = riskLabels(t);
  const level = levelOf(hazard.probability, hazard.severity);
  const set = patch => onChange({ ...hazard, ...patch });
  const setMeasure = (i, patch) =>
    set({ measures: hazard.measures.map((m, j) => (j === i ? { ...m, ...patch } : m)) });

  return (
    <div className="sf-hazard">
      <div className="sf-hazard-head">
        <Typography.Text strong>{index + 1}.</Typography.Text>
        <Input
          value={hazard.hazard}
          disabled={!editable}
          placeholder={t("Hazard")}
          maxLength={500}
          onChange={event => set({ hazard: event.target.value })}
        />
        {editable && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label={t("Delete")}
            onClick={onRemove}
          />
        )}
      </div>
      <Row gutter={[8, 8]} className="mb-2">
        <Col xs={24} md={10}>
          <Input
            value={hazard.category}
            disabled={!editable}
            placeholder={t("Kind of hazard (e.g. fall, electrical)")}
            maxLength={60}
            onChange={event => set({ category: event.target.value })}
          />
        </Col>
        <Col xs={9} md={5}>
          <Select
            className="sf-full"
            value={hazard.probability}
            disabled={!editable}
            onChange={value => set({ probability: value })}
            options={LEVELS.map(n => ({ value: n, label: `${t("Probability")} ${n}` }))}
          />
        </Col>
        <Col xs={9} md={5}>
          <Select
            className="sf-full"
            value={hazard.severity}
            disabled={!editable}
            onChange={value => set({ severity: value })}
            options={LEVELS.map(n => ({ value: n, label: `${t("Severity")} ${n}` }))}
          />
        </Col>
        <Col xs={6} md={4}>
          <Tag color={RISK_COLORS[level]} className="sf-risk">
            {risks[level]} ({hazard.probability * hazard.severity})
          </Tag>
        </Col>
      </Row>
      {hazard.measures.map((m, i) => (
        <Row gutter={[8, 8]} key={i} className="sf-measure">
          <Col xs={24} md={5}>
            <Select
              className="sf-full"
              value={m.type}
              disabled={!editable}
              onChange={value => setMeasure(i, { type: value })}
              options={Object.entries(measures).map(([value, label]) => ({
                value,
                label: `${value} – ${label}`
              }))}
            />
          </Col>
          <Col xs={24} md={9}>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 4 }}
              value={m.text}
              disabled={!editable}
              placeholder={t("Measure")}
              maxLength={1000}
              onChange={event => setMeasure(i, { text: event.target.value })}
            />
          </Col>
          <Col xs={12} md={4}>
            <Input
              value={m.responsible}
              disabled={!editable}
              placeholder={t("Responsible")}
              maxLength={120}
              onChange={event => setMeasure(i, { responsible: event.target.value })}
            />
          </Col>
          <Col xs={12} md={4}>
            <DatePicker
              className="sf-full"
              format="DD.MM.YYYY"
              value={dayValue(m.dueDate)}
              disabled={!editable}
              placeholder={t("By")}
              onChange={value => setMeasure(i, { dueDate: dayString(value) })}
            />
          </Col>
          <Col xs={24} md={2} className="sf-measure-end">
            <Checkbox
              checked={m.done}
              disabled={!editable}
              onChange={event => setMeasure(i, { done: event.target.checked })}
            >
              {t("Done")}
            </Checkbox>
            {editable && (
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                aria-label={t("Delete")}
                onClick={() => set({ measures: hazard.measures.filter((_, j) => j !== i) })}
              />
            )}
          </Col>
        </Row>
      ))}
      {editable && (
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() =>
            set({
              measures: [
                ...hazard.measures,
                { type: "O", text: "", responsible: "", dueDate: "", done: false }
              ]
            })
          }
        >
          {t("Add measure")}
        </Button>
      )}
    </div>
  );
};

/** One Gefährdungsbeurteilung: header fields, hazards with STOP measures, release and review. */
const AssessmentEditor = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { id } = useParams();
  const history = useHistory();
  const [meta, setMeta] = useState(null);
  const [doc, setDoc] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reason, setReason] = useState("");
  const statuses = assessmentStatusLabels(t);

  const show = data => {
    setDoc(data);
    setSaved(data);
  };

  useEffect(() => {
    Promise.all([getMeta(), getAssessment(id)])
      .then(([metaRes, res]) => {
        setMeta(metaRes.data);
        show(res.data.data);
      })
      .catch(generalErrorAlert);
  }, [id]);

  if (!doc || !meta) {
    return (
      <div className="safety-page sf-center">
        <Spin />
      </div>
    );
  }

  const editable = meta.can.update && doc.status === "draft";
  const dirty = JSON.stringify(doc) !== JSON.stringify(saved);
  const set = patch => setDoc(prev => ({ ...prev, ...patch }));
  const back = () => history.push("/user/safety?tab=assessments");

  const run = async (action, message) => {
    setBusy(true);
    try {
      const res = await action();
      if (res?.data?.data) show(res.data.data);
      if (message) Success(message);
      return res;
    } catch (error) {
      generalErrorAlert(error);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(
      () =>
        updateAssessment(doc._id, {
          title: doc.title,
          activity: doc.activity,
          site: doc.site,
          responsible: doc.responsible,
          assessedOn: doc.assessedOn,
          reviewDate: doc.reviewDate,
          note: doc.note,
          hazards: doc.hazards
        }),
      t("Saved")
    );

  const approve = async () => {
    if (dirty && !(await save())) return;
    await run(() => approveAssessment(doc._id), t("The risk assessment is now in force."));
  };

  const warnings = doc.summary?.warnings || [];
  const warningText = {
    NO_HAZARDS: t("Add at least one hazard."),
    HAZARD_WITHOUT_MEASURE: t("Every hazard needs at least one measure."),
    NO_REVIEW_DATE: t("Set a review date.")
  };

  return (
    <div className="safety-page">
      <div className="sf-header mb-3">
        <Button icon={<ArrowLeftOutlined />} onClick={back} aria-label={t("Back")} />
        <div className="sf-header-title">
          <Typography.Title level={4}>
            {doc.number} {doc.title}
          </Typography.Title>
          <Tag color={doc.status === "active" ? "success" : "default"}>{statuses[doc.status]}</Tag>
        </div>
        <Space wrap>
          <Button icon={<FilePdfOutlined />} href={assessmentPdfUrl(doc._id)} target="_blank">
            PDF
          </Button>
          {editable && (
            <Button icon={<SaveOutlined />} disabled={!dirty} loading={busy} onClick={save}>
              {t("Save")}
            </Button>
          )}
          {editable && (
            <Button type="primary" icon={<CheckCircleOutlined />} loading={busy} onClick={approve}>
              {t("Put in force")}
            </Button>
          )}
          {meta.can.update && doc.status !== "draft" && (
            <Button icon={<EditOutlined />} onClick={() => setReviseOpen(true)}>
              {t("Revise")}
            </Button>
          )}
          {meta.can.update && doc.status === "active" && (
            <Popconfirm
              title={t("Archive this risk assessment?")}
              onConfirm={() => run(() => archiveAssessment(doc._id))}
            >
              <Button icon={<InboxOutlined />}>{t("Archive")}</Button>
            </Popconfirm>
          )}
          {meta.can.delete && doc.status === "draft" && !doc.approvedAt && (
            <Popconfirm
              title={t("Delete this risk assessment?")}
              onConfirm={async () => {
                const res = await run(() => deleteAssessment(doc._id));
                if (res) back();
              }}
            >
              <Button danger icon={<DeleteOutlined />} aria-label={t("Delete")} />
            </Popconfirm>
          )}
        </Space>
      </div>

      {editable && warnings.length > 0 && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={warnings.map(w => warningText[w]).join(" ")}
        />
      )}
      {doc.status === "active" && (
        <Alert
          className="mb-3"
          type="success"
          showIcon
          message={t("In force since {{date}} ({{name}})", {
            date: formatDateTime(doc.approvedAt, i18n.language),
            name: doc.approvedByName
          })}
        />
      )}

      <Card size="small" className="mb-3">
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <label className="sf-label">{t("Title")}</label>
            <Input
              value={doc.title}
              disabled={!editable}
              maxLength={200}
              onChange={e => set({ title: e.target.value })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label className="sf-label">{t("Activity")}</label>
            <Input
              value={doc.activity}
              disabled={!editable}
              maxLength={300}
              onChange={e => set({ activity: e.target.value })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label className="sf-label">{t("Site")}</label>
            <Input
              value={doc.site}
              disabled={!editable}
              maxLength={300}
              onChange={e => set({ site: e.target.value })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label className="sf-label">{t("Responsible")}</label>
            <Input
              value={doc.responsible}
              disabled={!editable}
              maxLength={120}
              onChange={e => set({ responsible: e.target.value })}
            />
          </Col>
          <Col xs={12}>
            <label className="sf-label">{t("Assessed on")}</label>
            <DatePicker
              className="sf-full"
              format="DD.MM.YYYY"
              value={dayValue(doc.assessedOn)}
              disabled={!editable}
              onChange={value => set({ assessedOn: dayString(value) })}
            />
          </Col>
          <Col xs={12}>
            <label className="sf-label">{t("Review by")}</label>
            <DatePicker
              className="sf-full"
              format="DD.MM.YYYY"
              value={dayValue(doc.reviewDate)}
              disabled={!editable}
              onChange={value => set({ reviewDate: dayString(value) })}
            />
          </Col>
        </Row>
      </Card>

      <Card size="small" title={t("Hazards and measures")} className="mb-3">
        {doc.hazards.map((hazard, index) => (
          <HazardCard
            key={index}
            index={index}
            hazard={hazard}
            editable={editable}
            onChange={next => set({ hazards: doc.hazards.map((h, i) => (i === index ? next : h)) })}
            onRemove={() => set({ hazards: doc.hazards.filter((_, i) => i !== index) })}
          />
        ))}
        {editable && (
          <Button
            icon={<PlusOutlined />}
            onClick={() =>
              set({
                hazards: [
                  ...doc.hazards,
                  { hazard: "", category: "", probability: 2, severity: 2, measures: [] }
                ]
              })
            }
          >
            {t("Add hazard")}
          </Button>
        )}
        <Typography.Paragraph type="secondary" className="sf-small mt-3">
          {t(
            "Order of measures (STOP): substitution before technical before organisational before personal protective equipment."
          )}
        </Typography.Paragraph>
      </Card>

      <Card size="small" title={t("Note")}>
        <Input.TextArea
          autoSize={{ minRows: 2, maxRows: 8 }}
          value={doc.note}
          disabled={!editable}
          maxLength={10000}
          onChange={e => set({ note: e.target.value })}
        />
      </Card>

      <Modal
        rootClassName="safety-page"
        open={reviseOpen}
        title={t("Revise risk assessment")}
        okText={t("Revise")}
        cancelText={t("Cancel")}
        okButtonProps={{ disabled: reason.trim().length < 3 }}
        onCancel={() => setReviseOpen(false)}
        onOk={async () => {
          const res = await run(() => reviseAssessment(doc._id, reason));
          if (res) {
            setReviseOpen(false);
            setReason("");
          }
        }}
      >
        <Typography.Paragraph type="secondary">
          {t("The assessment goes back to draft. Put it in force again after the changes.")}
        </Typography.Paragraph>
        <Input.TextArea
          value={reason}
          placeholder={t("Reason (e.g. yearly review, new machine)")}
          autoSize={{ minRows: 2 }}
          onChange={e => setReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default AssessmentEditor;
